import React from "react";
import { useToast } from '../utils';

ContactInfoForm.defaultProps = {
  onSave: () => {},
  saveStatus: "saved",
  inviteStatus: "idle",
};

export default function ContactInfoForm({ client, onChange, onSendInvite, onSave, saveStatus, inviteStatus }) {
  const { toastMessage, showToast } = useToast();

  // Only keep the invite status toast effect
  React.useEffect(() => {
    if (inviteStatus === "sent") {
      showToast("Portal invite sent!");
    } else if (inviteStatus === "error") {
      showToast("Failed to send portal invite.", 5000);
    }
  }, [inviteStatus, showToast]);

  const renderField = (name, options = {}) => {
    const { type = "text", placeholder = "", required = false } = options;
    
    // Convert PascalCase field name to camelCase for state
    const camelCase = name.charAt(0).toLowerCase() + name.slice(1);
    
    // Convert field name to proper label (e.g., firstName -> First Name)
    const label = camelCase
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
    
    // Get value from camelCase field
    let value = client[camelCase] || '';
    
    // Format date value if it's a date field
    if (type === 'date' && value) {
      value = value.split('T')[0];
    }
    
    return (
      <div key={name}>
        <label>{label}</label>
        <input
          type={type}
          name={camelCase}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
        />
      </div>
    );
  };

  const renderSection = (title, fields, gridCols = "grid-cols-1 sm:grid-cols-2 md:grid-cols-3") => (
    <div className="border rounded-xl p-4 bg-gray-50">
      <h3 className="font-semibold text-emerald-700 mb-2">{title}</h3>
      <div className={`grid ${gridCols} gap-4`}>
        {fields.map(field => 
          typeof field === 'string' 
            ? renderField(field)
            : renderField(field.name, field.options)
        )}
      </div>
    </div>
  );
  
  return (
    <form className="space-y-6 bg-white p-4 rounded-xl shadow text-sm">
      {/* Client Info */}
      <div className="border rounded-xl p-4 bg-gray-50">
        <h3 className="font-semibold text-emerald-700 mb-2">Client Info</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label>Client Type</label>
            <input 
              name="clientType" 
              value={client.clientType || ''} 
              onChange={onChange} 
              className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full" 
            />
          </div>
          <div>
            <label>Client Status</label>
            <select
              name="clientStatus"
              value={client.clientStatus || ''}
              onChange={onChange}
              className="shadow-sm border border-gray-300 rounded px-2 py-1 w-full"
            >
              <option value="">Select status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="flex flex-col justify-end">
            <label className="invisible">Send Invite</label>
            <button
              type="button"
              onClick={onSendInvite}
              disabled={inviteStatus === "sending"}
              className={`px-3 py-1 rounded text-white ${
                inviteStatus === "sending"
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {inviteStatus === "sending" ? "Sending…" : "Send Portal Invite"}
            </button>
          </div>
        </div>
      </div>

      {/* Referral */}
      {renderSection("Referral Details", [
        'referredBy',
        'referredByDescription',
        'clientMRN'
      ])}

      {/* Name & Identity */}
      {renderSection("Identity", [
        'firstName',
        'lastName',
        'placeOfBirth',
        'otherName',
        { name: 'ssn', options: { placeholder: 'XXX-XX-XXXX' } },
        { name: 'dateOfBirth', options: { type: 'date' } }
      ])}

      {/* Contact Info */}
      {renderSection("Contact", [
        { name: 'homePhone', options: { placeholder: '(XXX) XXX-XXXX' } },
        { name: 'mobilePhone', options: { placeholder: '(XXX) XXX-XXXX' } },
        { name: 'email', options: { type: 'email' } }
      ])}

      {/* Address */}
      {renderSection("Address", [
        'street',
        'city',
        'state',
        { name: 'zip', options: { placeholder: 'XXXXX' } }
      ], "grid-cols-1 sm:grid-cols-2 md:grid-cols-4")}

      {/* Demographics */}
      {renderSection("Demographics", [
        'sexAtBirth',
        'maidenName',
        'relationshipStatus',
        'religiousPreference',
        'primaryLanguage'
      ])}

      {/* Race & Ethnicity */}
      {renderSection("Race & Ethnicity", [
        'race',
        'otherRaces',
        'ethnicity',
        'otherEthnicities'
      ], "grid-cols-1 sm:grid-cols-2")}

      {/* Education & Employment */}
      {renderSection("Education & Employment", [
        'educationStatus',
        'employmentStatus',
        'occupation',
        'employerOrSchool',
        'employerPhone'
      ])}

      {/* Opt-Ins */}
      <div className="border rounded-xl p-4 bg-gray-50">
        <h3 className="font-semibold text-emerald-700 mb-2">Preferences</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label>Mailing List</label>
            <div className="flex gap-4">
              <label><input type="radio" name="mailingList" value="yes" checked={client.mailingList === 'yes'} onChange={onChange} /> Yes</label>
              <label><input type="radio" name="mailingList" value="no" checked={client.mailingList === 'no'} onChange={onChange} /> No</label>
            </div>
          </div>
          <div>
            <label>Text Campaign List</label>
            <div className="flex gap-4">
              <label><input type="radio" name="textCampaignList" value="yes" checked={client.textCampaignList === 'yes'} onChange={onChange} /> Yes</label>
              <label><input type="radio" name="textCampaignList" value="no" checked={client.textCampaignList === 'no'} onChange={onChange} /> No</label>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="border rounded-xl p-4 bg-gray-50">
        <h3 className="font-semibold text-emerald-700 mb-2">Emergency Contact</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label>Emergency Contact Name</label>
            <input name="emergencyContact" value={client.emergencyContact || ''} onChange={onChange} className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full" />
          </div>
          <div>
            <label>Emergency Contact Phone</label>
            <input name="emergencyContactPhone" value={client.emergencyContactPhone || ''} onChange={onChange} className="input shadow-sm border border-gray-300 rounded px-2 py-1 w-full" />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={onSave}
          className={`px-4 py-2 rounded text-white text-sm font-medium ${
            saveStatus === 'saving'
              ? 'bg-yellow-400'
              : saveStatus === 'error'
              ? 'bg-red-500'
              : 'bg-emerald-600 hover:bg-emerald-700'
          }`}
          disabled={saveStatus === 'saving'}
        >
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'error' ? 'Retry Save' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
